import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Play, History, BookOpen, Zap, Clock, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight, Search, Package, FileOutput, Download, Square, CheckSquare, Eye } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend } from 'recharts';
import SWMPriorityWizard from './SWMPriorityWizard';
import AllocationPreviewModal from './AllocationPreviewModal';
import SoftAllocationPreviewModal from './SoftAllocationPreviewModal';
import ShortagePRDraftModal from './ShortagePRDraftModal';

const STATUS_COLORS = {
  UNALLOCATED: { bg: '#ef444420', text: '#ef4444' },
  PARTIAL_SOFT: { bg: '#eab30820', text: '#eab308' },
  READY_SOFT: { bg: '#3b82f620', text: '#3b82f6' },
  PO_READY: { bg: '#143A6B20', text: '#143A6B' },
  PARTIAL_HARD: { bg: '#f59e0b20', text: '#f59e0b' },
  FULLY_ALLOCATED: { bg: '#22c55e20', text: '#22c55e' },
};

const RUN_STATUS_COLORS = {
  RUNNING: '#f59e0b', COMPLETED: '#22c55e', FAILED: '#ef4444', COMPLETED_WITH_WARNINGS: '#f59e0b',
};

export default function SWMAllocationEngine({ projectId }) {
  const [tab, setTab] = useState('engine');
  const [running, setRunning] = useState(null);
  const [stateDist, setStateDist] = useState({});
  const [runs, setRuns] = useState([]);
  const [ledger, setLedger] = useState({ entries: [], total: 0 });
  const [ledgerFilter, setLedgerFilter] = useState({ allocationType: '', structuralTag: '', markNumber: '' });
  const [expandedRun, setExpandedRun] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [dealloc, setDealloc] = useState({ markNumber: '', mCode: '', quantity: '', reason: '', targetMarkNumber: '' });
  const [deallocating, setDeallocating] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [invChartFilter, setInvChartFilter] = useState(null);
  const [pendingIssues, setPendingIssues] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [issuingMark, setIssuingMark] = useState(null);
  const [issuingTag, setIssuingTag] = useState(null);
  const [expandedIssueTag, setExpandedIssueTag] = useState(null);
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [selectedMarks, setSelectedMarks] = useState(new Set());
  const [issuingSelected, setIssuingSelected] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSoftPreviewModal, setShowSoftPreviewModal] = useState(false);
  const [showShortageModal, setShowShortageModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [sumRes, runsRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/swm/summary`),
        api.get(`/v1/projects/${projectId}/swm/allocation/runs?limit=20`),
      ]);
      setStateDist(sumRes.data?.stateDistribution || {});
      setRuns(runsRes.data || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadLedger = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (ledgerFilter.allocationType) params.set('allocationType', ledgerFilter.allocationType);
      if (ledgerFilter.structuralTag) params.set('structuralTag', ledgerFilter.structuralTag);
      if (ledgerFilter.markNumber) params.set('markNumber', ledgerFilter.markNumber);
      const res = await api.get(`/v1/projects/${projectId}/swm/allocation/ledger?${params}`);
      setLedger(res.data || { entries: [], total: 0 });
    } catch { /* ignore */ }
  }, [projectId, ledgerFilter]);

  const loadForecast = useCallback(async () => {
    setForecastLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/forecast/latest`);
      setForecast(res.data);
    } catch { setForecast(null); }
    setForecastLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (tab === 'ledger') loadLedger(); }, [tab, loadLedger]);
  useEffect(() => { if (tab === 'forecast') loadForecast(); }, [tab, loadForecast]);

  const loadInventory = useCallback(async () => {
    setInventoryLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/allocation/inventory-summary`);
      setInventory(res.data);
    } catch { /* ignore */ }
    setInventoryLoading(false);
  }, [projectId]);

  useEffect(() => { if (tab === 'inventory') loadInventory(); }, [tab, loadInventory]);

  const loadPendingIssues = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/material-issue/pending`);
      setPendingIssues(res.data);
    } catch { setPendingIssues(null); }
    setPendingLoading(false);
  }, [projectId]);

  useEffect(() => { /* Material Issue tab removed — issue flow lives in Material Tracking. */ }, []);

  const issueSingleMark = async (markNumber) => {
    setIssuingMark(markNumber);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/material-issue/mark/${markNumber}`);
      toast.success(`Issued ${markNumber} — MIR: ${res.data.mirNumber}`);
      loadPendingIssues();
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Issue failed');
    }
    setIssuingMark(null);
  };

  const issueEntireTag = async (tag) => {
    setIssuingTag(tag);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/material-issue/tag/${tag}`);
      toast.success(`Issued ${res.data.issued} of ${res.data.total} marks for ${tag}`);
      loadPendingIssues();
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Bulk issue failed');
    }
    setIssuingTag(null);
  };

  const toggleTagSelection = (tag, marksInTag) => {
    const newTags = new Set(selectedTags);
    const newMarks = new Set(selectedMarks);
    if (newTags.has(tag)) {
      newTags.delete(tag);
      marksInTag.forEach(m => newMarks.delete(m.markNumber));
    } else {
      newTags.add(tag);
      marksInTag.forEach(m => newMarks.add(m.markNumber));
    }
    setSelectedTags(newTags);
    setSelectedMarks(newMarks);
  };

  const toggleMarkSelection = (markNumber, tag, totalMarksInTag) => {
    const newMarks = new Set(selectedMarks);
    const newTags = new Set(selectedTags);
    if (newMarks.has(markNumber)) {
      newMarks.delete(markNumber);
      newTags.delete(tag);
    } else {
      newMarks.add(markNumber);
      // Check if all marks in tag are now selected
      const group = pendingIssues?.tagGroups?.[tag];
      if (group && group.marks.every(m => newMarks.has(m.markNumber))) {
        newTags.add(tag);
      }
    }
    setSelectedTags(newTags);
    setSelectedMarks(newMarks);
  };

  const toggleSelectAll = () => {
    if (!pendingIssues?.tagGroups) return;
    const allTags = Object.keys(pendingIssues.tagGroups);
    const allMarks = Object.values(pendingIssues.tagGroups).flatMap(g => g.marks.map(m => m.markNumber));
    if (selectedMarks.size === allMarks.length) {
      setSelectedTags(new Set());
      setSelectedMarks(new Set());
    } else {
      setSelectedTags(new Set(allTags));
      setSelectedMarks(new Set(allMarks));
    }
  };

  const issueSelectedItems = async () => {
    if (selectedMarks.size === 0) return;
    setIssuingSelected(true);
    let issued = 0, failed = 0;
    // Issue by tag for full-tag selections, individual marks otherwise
    const tagGroups = pendingIssues?.tagGroups || {};
    const processedMarks = new Set();
    for (const tag of selectedTags) {
      const group = tagGroups[tag];
      if (group && group.marks.every(m => selectedMarks.has(m.markNumber))) {
        try {
          const res = await api.post(`/v1/projects/${projectId}/swm/material-issue/tag/${tag}`);
          issued += res.data.issued || 0;
          group.marks.forEach(m => processedMarks.add(m.markNumber));
        } catch { failed++; }
      }
    }
    // Issue remaining individual marks not covered by bulk tag issue
    for (const mn of selectedMarks) {
      if (processedMarks.has(mn)) continue;
      try {
        await api.post(`/v1/projects/${projectId}/swm/material-issue/mark/${mn}`);
        issued++;
      } catch { failed++; }
    }
    toast.success(`Issued ${issued} marks${failed > 0 ? `, ${failed} failed` : ''}`);
    setSelectedTags(new Set());
    setSelectedMarks(new Set());
    setIssuingSelected(false);
    loadPendingIssues();
    loadData();
  };

  const exportMirRegister = async () => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/material-issue/register/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `MIR_Register_${projectId}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('MIR Register exported');
    } catch { toast.error('Export failed'); }
  };

  const runAllocation = async (type) => {
    setRunning(type);
    try {
      const endpoint = type === 'soft' ? 'run-soft' : type === 'hard' ? 'run-hard' : 'run-full';
      const res = await api.post(`/v1/projects/${projectId}/swm/allocation/${endpoint}`);
      const label = `${type.charAt(0).toUpperCase() + type.slice(1)} allocation`;
      setLastRun({ type, label, ...(res.data || {}) });
      const affected = res.data?.marksUpdated ?? res.data?.tagsUpdated ?? res.data?.updated ?? res.data?.count ?? 0;
      const qty = res.data?.qtyAllocated ?? res.data?.totalAllocated ?? res.data?.weightAllocated ?? 0;
      toast.success(`${label} completed · ${affected} record(s) updated${qty ? ` · ${qty} qty allocated` : ''}`);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Allocation failed'); }
    setRunning(null);
  };

  const generateForecast = async () => {
    setForecastLoading(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/forecast/generate`);
      setForecast(res.data);
      toast.success('Forecast generated');
    } catch (e) { toast.error('Forecast generation failed'); }
    setForecastLoading(false);
  };

  const handleDealloc = async () => {
    if (!dealloc.markNumber || !dealloc.reason) { toast.error('Mark number and reason required'); return; }
    setDeallocating(true);
    try {
      await api.post(`/v1/projects/${projectId}/swm/allocation/de-allocate`, {
        markNumber: dealloc.markNumber, mCode: dealloc.mCode || null,
        quantity: dealloc.quantity ? Number(dealloc.quantity) : null, reason: dealloc.reason,
        targetMarkNumber: dealloc.targetMarkNumber || null,
      });
      toast.success(`De-allocated ${dealloc.markNumber}`);
      setDealloc({ markNumber: '', mCode: '', quantity: '', reason: '', targetMarkNumber: '' });
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'De-allocation failed');
    }
    setDeallocating(false);
  };

  const tabs = [
    { id: 'engine', label: 'Run Engine', icon: Zap },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'runs', label: 'Run History', icon: History },
    { id: 'ledger', label: 'Ledger', icon: BookOpen },
    { id: 'forecast', label: 'Forecast', icon: Clock },
  ];
  // Material Issue moved to Material Tracking → Field Issue.
  // The old sub-tab is intentionally removed from SWM per user request.

  const totalTags = Object.values(stateDist).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-3" data-testid="swm-allocation-engine">
      {showWizard && <SWMPriorityWizard projectId={projectId} onClose={() => { setShowWizard(false); loadData(); }} />}

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b pb-1" style={{ borderColor: 'var(--border-color)' }}>
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium rounded-t-md transition-colors" data-testid={`swm-alloc-tab-${t.id}`}
              style={{ background: tab === t.id ? 'var(--card-bg)' : 'transparent', color: tab === t.id ? '#F59206' : 'var(--text-muted)', borderBottom: tab === t.id ? '2px solid #F59206' : '2px solid transparent' }}>
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* RUN ENGINE */}
      {tab === 'engine' && (
        <div className="space-y-3">
          {/* State Distribution */}
          <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <p className="text-[10px] font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Allocation State Distribution ({totalTags} tags)</p>
            <div className="flex h-4 rounded-full overflow-hidden mb-2" style={{ background: 'var(--card-bg-alt)' }}>
              {Object.entries(STATUS_COLORS).map(([key, cfg]) => {
                const camelKey = key === 'UNALLOCATED' ? 'unallocated' : key === 'PARTIAL_SOFT' ? 'partialSoft' : key === 'READY_SOFT' ? 'readySoft' : key === 'PO_READY' ? 'poReady' : key === 'PARTIAL_HARD' ? 'partialHard' : 'fullyAllocated';
                const val = stateDist[camelKey] || 0;
                const pct = totalTags > 0 ? (val / totalTags * 100) : 0;
                if (pct === 0) return null;
                return <div key={key} style={{ width: `${pct}%`, background: cfg.text }} title={`${key}: ${val}`} />;
              })}
            </div>
            <div className="flex items-center gap-3 flex-wrap text-[10px]">
              {Object.entries(STATUS_COLORS).map(([key, cfg]) => {
                const camelKey = key === 'UNALLOCATED' ? 'unallocated' : key === 'PARTIAL_SOFT' ? 'partialSoft' : key === 'READY_SOFT' ? 'readySoft' : key === 'PO_READY' ? 'poReady' : key === 'PARTIAL_HARD' ? 'partialHard' : 'fullyAllocated';
                return (
                  <span key={key} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm" style={{ background: cfg.text }} />
                    <span style={{ color: 'var(--text-muted)' }}>{key.replace(/_/g, ' ')}:</span>
                    <span className="font-bold" style={{ color: cfg.text }}>{stateDist[camelKey] || 0}</span>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Run Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <button onClick={() => setShowPreviewModal(true)} disabled={!!running}
              className="p-3 rounded-lg border text-left hover:brightness-95 transition-all"
              style={{ background: '#F5920608', borderColor: '#F5920680', boxShadow: '0 1px 0 rgba(245,146,6,.15) inset' }}
              data-testid="swm-preview-hard">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4" style={{ color: '#F59206' }} />
                <span className="text-[11px] font-bold" style={{ color: '#F59206' }}>Preview Hard Allocation</span>
              </div>
              <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Human-in-the-loop: review, edit & approve before commit</p>
            </button>
            <button onClick={() => setShowSoftPreviewModal(true)} disabled={!!running}
              className="p-3 rounded-lg border text-left hover:brightness-95 transition-all"
              style={{ background: '#3b82f608', borderColor: '#3b82f680', boxShadow: '0 1px 0 rgba(59,130,246,.15) inset' }}
              data-testid="swm-preview-soft">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4" style={{ color: '#3b82f6' }} />
                <span className="text-[11px] font-bold" style={{ color: '#3b82f6' }}>Preview Soft Allocation</span>
              </div>
              <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>PO-released − IQC-accepted; priority-aware, review before commit</p>
            </button>
            <button onClick={() => runAllocation('hard')} disabled={!!running}
              className="p-3 rounded-lg border text-left hover:brightness-95 transition-all" style={{ background: '#22c55e08', borderColor: '#22c55e30' }} data-testid="swm-run-hard">
              <div className="flex items-center gap-2 mb-1">
                {running === 'hard' ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#22c55e' }} /> : <Play className="w-4 h-4" style={{ color: '#22c55e' }} />}
                <span className="text-[11px] font-bold" style={{ color: '#22c55e' }}>Auto Hard Allocation</span>
              </div>
              <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Legacy autonomous mode — bypasses preview (not recommended)</p>
            </button>
            <button onClick={() => runAllocation('full')} disabled={!!running}
              className="p-3 rounded-lg border text-left hover:brightness-95 transition-all" style={{ background: '#F5920608', borderColor: '#F5920630' }} data-testid="swm-run-full">
              <div className="flex items-center gap-2 mb-1">
                {running === 'full' ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#F59206' }} /> : <Zap className="w-4 h-4" style={{ color: '#F59206' }} />}
                <span className="text-[11px] font-bold" style={{ color: '#F59206' }}>Run Full (Soft + Hard)</span>
              </div>
              <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Complete allocation cycle: PO reservation then stock allocation</p>
            </button>
          </div>

          {showPreviewModal && (
            <AllocationPreviewModal projectId={projectId}
              onClose={() => setShowPreviewModal(false)}
              onCommitted={() => { loadData(); loadInventory(); }} />
          )}
          {showSoftPreviewModal && (
            <SoftAllocationPreviewModal projectId={projectId}
              onClose={() => setShowSoftPreviewModal(false)}
              onCommitted={() => { loadData(); loadInventory(); }} />
          )}
          {showShortageModal && (
            <ShortagePRDraftModal projectId={projectId}
              onClose={() => setShowShortageModal(false)} />
          )}

          {/* Allocation Analysis — appears after any Execute button completes */}
          {lastRun && (
            <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }} data-testid="swm-last-run-analysis">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>Last Run · {lastRun.label}</p>
                <button onClick={() => setLastRun(null)} className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Dismiss</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
                {[
                  ['Marks Updated',   lastRun.marksUpdated],
                  ['Tags Updated',    lastRun.tagsUpdated],
                  ['Qty Allocated',   lastRun.qtyAllocated ?? lastRun.totalAllocated ?? lastRun.weightAllocated],
                  ['Fully Allocated', lastRun.fullyAllocated],
                  ['Partial',         lastRun.partial],
                  ['Warnings',        Array.isArray(lastRun.warnings) ? lastRun.warnings.length : lastRun.warnings],
                  ['Run ID',          lastRun.runId ?? lastRun.id],
                  ['Duration (ms)',   lastRun.durationMs],
                ].filter(([_, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
                  <div key={k} className="p-2 rounded" style={{ background: 'var(--bg-input)' }}>
                    <p style={{ color: 'var(--text-muted)' }}>{k}</p>
                    <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{typeof v === 'number' ? v.toLocaleString() : String(v)}</p>
                  </div>
                ))}
              </div>
              {Array.isArray(lastRun.warnings) && lastRun.warnings.length > 0 && (
                <ul className="mt-2 text-[10px] space-y-0.5" style={{ color: '#f59e0b' }}>
                  {lastRun.warnings.slice(0, 5).map((w, i) => <li key={i}>• {typeof w === 'string' ? w : (w.message || JSON.stringify(w))}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Priority Wizard + De-alloc */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
              <p className="text-[11px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Priority Wizard</p>
              <p className="text-[9px] mb-2" style={{ color: 'var(--text-muted)' }}>Assign/edit priorities for structural tags with 5-step guided flow</p>
              <Button size="sm" onClick={() => setShowWizard(true)} className="text-[10px] h-7" style={{ background: '#F59206' }} data-testid="swm-open-wizard">
                Open Wizard
              </Button>
            </div>
            <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
              <p className="text-[11px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>De-Allocate Mark</p>
              <div className="flex gap-1 flex-wrap">
                <input value={dealloc.markNumber} onChange={e => setDealloc(d => ({ ...d, markNumber: e.target.value }))} placeholder="Mark #" className="text-[10px] px-2 py-1 rounded border w-20" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="dealloc-mark" />
                <input value={dealloc.reason} onChange={e => setDealloc(d => ({ ...d, reason: e.target.value }))} placeholder="Reason" className="text-[10px] px-2 py-1 rounded border flex-1" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="dealloc-reason" />
                <Button size="sm" onClick={handleDealloc} disabled={deallocating} className="text-[10px] h-7" variant="destructive" data-testid="dealloc-submit">
                  {deallocating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'De-Alloc'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MATERIAL ISSUE */}
      {tab === 'issue' && (
        <div className="space-y-3" data-testid="swm-material-issue">
          {pendingLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
          ) : pendingIssues ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border" style={{ borderColor: '#f59e0b30', background: '#f59e0b08' }}>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Pending Issue</p>
                  <p className="text-xl font-bold font-mono" style={{ color: '#f59e0b' }}>{pendingIssues.totalPending}</p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>marks ready to issue</p>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: '#3b82f630', background: '#3b82f608' }}>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Tags with Pending</p>
                  <p className="text-xl font-bold font-mono" style={{ color: '#3b82f6' }}>{pendingIssues.tagCount}</p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>structural tags</p>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: '#22c55e30', background: '#22c55e08' }}>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Status</p>
                  <p className="text-sm font-bold" style={{ color: pendingIssues.totalPending > 0 ? '#f59e0b' : '#22c55e' }}>
                    {pendingIssues.totalPending > 0 ? 'Action Required' : 'All Issued'}
                  </p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>100% allocated, not yet issued</p>
                </div>
              </div>

              {/* Action Bar: Select All + Issue Selected + Export */}
              {pendingIssues.totalPending > 0 && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center gap-3">
                    <button onClick={toggleSelectAll} className="flex items-center gap-1.5 text-[10px] font-medium" style={{ color: 'var(--text-primary)' }} data-testid="swm-select-all">
                      {selectedMarks.size > 0 && selectedMarks.size === pendingIssues.totalPending ? (
                        <CheckSquare className="w-3.5 h-3.5" style={{ color: '#F59206' }} />
                      ) : (
                        <Square className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                      )}
                      Select All ({pendingIssues.totalPending})
                    </button>
                    {selectedMarks.size > 0 && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: '#F5920620', color: '#F59206' }}>
                        {selectedMarks.size} selected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedMarks.size > 0 && (
                      <Button size="sm" onClick={issueSelectedItems} disabled={issuingSelected} className="text-[10px] h-6"
                        style={{ background: '#22c55e' }} data-testid="swm-issue-selected">
                        {issuingSelected ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileOutput className="w-3 h-3 mr-1" />}
                        Issue Selected ({selectedMarks.size})
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={exportMirRegister} className="text-[10px] h-6" data-testid="swm-export-mir">
                      <Download className="w-3 h-3 mr-1" /> Export MIR Register
                    </Button>
                  </div>
                </div>
              )}

              {pendingIssues.totalPending === 0 ? (
                <div className="p-6 rounded-lg border text-center" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#22c55e' }} />
                  <p className="text-sm font-bold" style={{ color: '#22c55e' }}>All Materials Issued</p>
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>No marks pending material issue. Run allocation to generate new eligible marks.</p>
                  <Button size="sm" variant="outline" onClick={exportMirRegister} className="text-[10px] h-6 mt-3" data-testid="swm-export-mir-empty">
                    <Download className="w-3 h-3 mr-1" /> Export MIR Register
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(pendingIssues.tagGroups || {}).map(([tag, group]) => {
                    const isExpanded = expandedIssueTag === tag;
                    const tagChecked = selectedTags.has(tag);
                    return (
                      <div key={tag} className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: tagChecked ? '#F59206' : 'var(--border-color)' }} data-testid={`issue-tag-${tag}`}>
                        <div className="flex items-center justify-between px-3 py-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleTagSelection(tag, group.marks)} data-testid={`issue-check-tag-${tag}`}>
                              {tagChecked ? (
                                <CheckSquare className="w-3.5 h-3.5" style={{ color: '#F59206' }} />
                              ) : (
                                <Square className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                              )}
                            </button>
                            <button className="flex items-center gap-2" onClick={() => setExpandedIssueTag(isExpanded ? null : tag)}>
                              {isExpanded ? <ChevronDown className="w-3 h-3" style={{ color: '#F59206' }} /> : <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
                              <span className="text-[11px] font-mono font-bold" style={{ color: '#F59206' }}>{tag}</span>
                              <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: '#f59e0b20', color: '#f59e0b' }}>{group.count} marks</span>
                            </button>
                          </div>
                          <Button size="sm" onClick={() => issueEntireTag(tag)} disabled={issuingTag === tag} className="text-[10px] h-6"
                            style={{ background: '#22c55e' }} data-testid={`issue-all-tag-${tag}`}>
                            {issuingTag === tag ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileOutput className="w-3 h-3 mr-1" />}
                            Issue All ({group.count})
                          </Button>
                        </div>
                        {isExpanded && (
                          <div style={{ borderTop: '1px solid var(--border-color)' }}>
                            <table className="w-full text-[10px]">
                              <thead>
                                <tr style={{ background: 'var(--card-bg-alt)' }}>
                                  <th className="px-3 py-1.5 w-8"></th>
                                  <th className="px-3 py-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Mark #</th>
                                  <th className="px-3 py-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>mCode</th>
                                  <th className="px-3 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Weight</th>
                                  <th className="px-3 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Alloc %</th>
                                  <th className="px-3 py-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.marks.map(m => {
                                  const markChecked = selectedMarks.has(m.markNumber);
                                  return (
                                    <tr key={m.markNumber} style={{ borderTop: '1px solid var(--border-color)', background: markChecked ? '#F5920608' : 'transparent' }} data-testid={`issue-row-${m.markNumber}`}>
                                      <td className="px-3 py-1.5 text-center">
                                        <button onClick={() => toggleMarkSelection(m.markNumber, tag, group.count)} data-testid={`issue-check-${m.markNumber}`}>
                                          {markChecked ? (
                                            <CheckSquare className="w-3 h-3" style={{ color: '#F59206' }} />
                                          ) : (
                                            <Square className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                                          )}
                                        </button>
                                      </td>
                                      <td className="px-3 py-1.5 font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{m.markNumber}</td>
                                      <td className="px-3 py-1.5" style={{ color: 'var(--text-muted)' }}>{m.mCode}</td>
                                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{(m.totalWeight || 0).toLocaleString()} kg</td>
                                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: '#22c55e' }}>{(m.overallAllocatedPercent || 0).toFixed(0)}%</td>
                                      <td className="px-3 py-1.5 text-center">
                                        <Button size="sm" onClick={() => issueSingleMark(m.markNumber)} disabled={issuingMark === m.markNumber}
                                          className="text-[9px] h-5 px-2" style={{ background: '#F59206' }} data-testid={`issue-btn-${m.markNumber}`}>
                                          {issuingMark === m.markNumber ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Issue'}
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No data available. Run allocation first.</p>
          )}
        </div>
      )}

      {/* INVENTORY */}
      {tab === 'inventory' && (
        <div className="space-y-3" data-testid="swm-inventory-summary">
          {inventoryLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
          ) : inventory ? (
            <>
              {/* MTM sync banner — surfaces how many m-codes are getting their
                  live counters from the MTM module (post-GRN intake). */}
              {(inventory.mtmSyncedMCount ?? 0) > 0 && (
                <div className="px-3 py-1.5 rounded-md text-[10px] flex items-center justify-between"
                  style={{ background: '#10b98115', border: '1px solid #10b98140', color: '#10b981' }}
                  data-testid="swm-inv-mtm-banner">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                    <strong>{inventory.mtmSyncedMCount}</strong> of {inventory.mCodeCount} material codes synced live from <strong>Material Tracking (MTM)</strong>
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    Reserved <strong>{(inventory.totals.mtmReserved || 0).toLocaleString()}</strong> · Awaiting Insp. <strong>{(inventory.totals.mtmAwaitingInspection || 0).toLocaleString()}</strong>
                  </span>
                </div>
              )}
              {/* Pipeline Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Tag MTO Required', value: inventory.totals.tagMtoQty, sub: `${inventory.mCodeCount} material codes`, color: '#F59206' },
                  { label: 'PO Ordered', value: inventory.totals.poOrdered, sub: `Weight: ${(inventory.totals.poOrderedWeight / 1000).toFixed(1)}T`, color: '#3b82f6' },
                  { label: 'Stock at Site', value: inventory.totals.stockQty, sub: `QA Accepted: ${inventory.totals.qaAccepted?.toLocaleString()}`, color: '#f59e0b' },
                  { label: 'Available After Alloc', value: inventory.totals.availableAfterAlloc, sub: `Hard Allocated: ${inventory.totals.hardAllocated?.toLocaleString()}`, color: '#22c55e' },
                  { label: 'Issued Qty', value: inventory.totals.issuedQty, sub: `Weight: ${((inventory.totals.issuedWeight || 0) / 1000).toFixed(1)}T`, color: '#143A6B' },
                  { label: 'Balance After Issue', value: inventory.totals.balanceAfterIssue, sub: `QA Accepted - Issued`, color: '#06b6d4' },
                ].map(c => (
                  <div key={c.label} className="p-3 rounded-lg border" style={{ borderColor: c.color + '30', background: c.color + '08' }}>
                    <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>{c.label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color: c.color }}>{c.value?.toLocaleString()}</p>
                    <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{c.sub}</p>
                  </div>
                ))}
              </div>

              {/* Orphan-shortage chip — surfaces mark demand hidden from
                  the strict MTX-live inventory view. */}
              {(inventory.orphanShortages?.codeCount ?? 0) > 0 && (
                <button
                  onClick={() => setShowShortageModal(true)}
                  className="w-full px-3 py-2 rounded-md text-[10.5px] flex items-center justify-between hover:brightness-95 transition"
                  style={{ background: '#F59E0B10', border: '1px solid #F59E0B60', color: '#B45309' }}
                  data-testid="swm-inv-shortage-chip">
                  <span className="flex items-center gap-2">
                    <span className="inline-flex w-2 h-2 rounded-full animate-pulse" style={{ background: '#F59E0B' }} />
                    <strong>{inventory.orphanShortages.totalMarksAffected}</strong> marks needing
                    <strong className="tabular-nums">{Number(inventory.orphanShortages.totalShortageQty).toLocaleString()}</strong>
                    units across <strong>{inventory.orphanShortages.codeCount}</strong> material codes — <span style={{ color: '#92400E' }}>no MTX supply yet</span>
                  </span>
                  <span className="font-semibold" style={{ color: '#B45309' }}>Draft Cognara PR →</span>
                </button>
              )}
              {/* Per-mCode Interactive Chart — legend on top, labels rotated + truncated */}
              <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Inventory by Material Code</p>
                  {invChartFilter && (
                    <button onClick={() => setInvChartFilter(null)} className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: '#F5920620', color: '#F59206' }}>
                      Showing: {invChartFilter} — Click to clear
                    </button>
                  )}
                </div>
                <div className="h-[300px]" data-testid="swm-inv-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(inventory.items || []).slice(0, 20)} margin={{ top: 8, right: 12, left: 0, bottom: 90 }}
                      onClick={(data) => {
                        if (data?.activeLabel) setInvChartFilter(prev => prev === data.activeLabel ? null : data.activeLabel);
                      }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis
                        dataKey="mCode"
                        tick={{ fill: 'var(--text-muted)', fontSize: 9 }}
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        interval={0}
                        tickFormatter={(v) => (v && v.length > 22 ? v.slice(0, 22) + '…' : v)}
                      />
                      <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 9 }} tickFormatter={(v) => v?.toLocaleString?.() ?? v} />
                      <Tooltip
                        contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 10 }}
                        labelFormatter={(label, payload) => {
                          const row = payload?.[0]?.payload;
                          return row?.section ? `${label} · ${row.section}` : label;
                        }}
                        formatter={(val, name) => [Number(val || 0).toLocaleString(), name]}
                      />
                      <Legend
                        verticalAlign="top"
                        align="center"
                        wrapperStyle={{ fontSize: 10, paddingBottom: 8 }}
                        iconSize={10}
                      />
                      <Bar dataKey="tagMtoQty" name="MTO Reqd" fill="#F59206" />
                      <Bar dataKey="poOrdered" name="PO Ordered" fill="#3b82f6" />
                      <Bar dataKey="inTransit" name="In Transit" fill="#eab308" />
                      <Bar dataKey="hardAllocated" name="Hard Alloc" fill="#22c55e" />
                      <Bar dataKey="issuedQty" name="Issued Qty" fill="#143A6B" />
                      <Bar dataKey="balanceAfterIssue" name="Balance After Issue" fill="#06b6d4" />
                      <Bar dataKey="availableAfterAlloc" name="Available" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Pipeline Flow Bar */}
              <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <p className="text-[10px] font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Inventory Pipeline Flow</p>
                <div className="flex gap-0.5 h-6 rounded-lg overflow-hidden">
                  {[
                    { label: 'Hard Alloc', val: inventory.totals.hardAllocated, color: '#22c55e' },
                    { label: 'Issued', val: inventory.totals.issuedQty, color: '#143A6B' },
                    { label: 'Balance', val: inventory.totals.balanceAfterIssue, color: '#06b6d4' },
                    { label: 'Available', val: inventory.totals.availableAfterAlloc, color: '#10b981' },
                    { label: 'In Transit', val: inventory.totals.inTransit, color: '#f59e0b' },
                    { label: 'Not Ordered', val: Math.max(inventory.totals.tagMtoQty - inventory.totals.poOrdered, 0), color: '#ef4444' },
                  ].filter(s => s.val > 0).map(s => {
                    const total = Math.max(inventory.totals.tagMtoQty, 1);
                    const pct = (s.val / total) * 100;
                    return (
                      <div key={s.label} title={`${s.label}: ${s.val.toLocaleString()} (${pct.toFixed(1)}%)`}
                        className="flex items-center justify-center text-[7px] font-bold text-white cursor-help transition-all hover:opacity-80"
                        style={{ background: s.color, width: `${Math.max(pct, 2)}%` }}>
                        {pct > 8 ? s.label : ''}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 mt-2">
                  {[
                    { label: 'Hard Allocated', color: '#22c55e' },
                    { label: 'Issued', color: '#143A6B' },
                    { label: 'Balance After Issue', color: '#06b6d4' },
                    { label: 'Available at Site', color: '#10b981' },
                    { label: 'In Transit', color: '#f59e0b' },
                    { label: 'Not Ordered', color: '#ef4444' },
                  ].map(l => (
                    <span key={l.label} className="flex items-center gap-1 text-[8px]" style={{ color: 'var(--text-muted)' }}>
                      <span className="w-2 h-2 rounded-sm" style={{ background: l.color }} /> {l.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Per-mCode Pipeline Table (filtered by chart click) */}
              <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
                <table className="w-full text-[10px]" data-testid="swm-inventory-table">
                  <thead>
                    <tr style={{ background: 'var(--card-bg-alt)' }}>
                      <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>mCode</th>
                      <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Section</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#F59206' }}>Tag MTO</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#a855f7' }}>Mark BoQ</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>PO Ordered</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#f59e0b' }}>In Transit</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#eab308', borderLeft: '2px solid var(--border-color)' }}>Stock Qty</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#0EA5E9' }} title="Units at CTR-BIN / OWN-BIN racks">WH Stock</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#0F766E' }} title="Units at SS-* site station racks (Fab / Erection Yard)">SS Stock</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#84cc16' }}>QA Accepted</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>Hard Alloc</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#10b981' }}>Avail After</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#143A6B', borderLeft: '2px solid var(--border-color)' }}>Issued Qty</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#06b6d4' }}>Bal After Issue</th>
                      <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>POs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invChartFilter ? inventory.items.filter(i => i.mCode === invChartFilter) : inventory.items).map(item => (
                      <tr key={item.mCode} style={{ borderTop: '1px solid var(--border-color)', background: invChartFilter === item.mCode ? '#F5920610' : 'var(--card-bg)' }}
                        data-testid={`inv-row-${item.mCode}`}>
                        <td className="p-2 font-mono font-semibold" style={{ color: '#F59206' }}>
                          {item.mCode}
                          {item.mtmLive && (
                            <span title={`Live MTM: ${item.mtmMaterialCount} material doc(s) · Reserved ${(item.mtmReserved||0).toLocaleString()} · Awaiting Insp. ${(item.mtmAwaitingInspection||0).toLocaleString()}`}
                              className="ml-1.5 px-1 py-0 rounded text-[7px] font-bold inline-flex items-center gap-0.5"
                              style={{ background: '#10b98120', color: '#10b981', border: '1px solid #10b98140' }}
                              data-testid={`mtm-live-${item.mCode}`}>
                              <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"/> MTM
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-[9px]" style={{ color: 'var(--text-muted)' }}>{item.section}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#F59206' }}>{item.tagMtoQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#a855f7' }}>{item.markBoqQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>{item.poOrdered?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#f59e0b' }}>{item.inTransit?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#eab308', borderLeft: '2px solid var(--border-color)' }}>{item.stockQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#0EA5E9' }}>{(item.warehouseStock ?? 0).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#0F766E' }}>{(item.siteStationStock ?? 0).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#84cc16' }}>{item.qaAccepted?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>{item.hardAllocated?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: item.availableAfterAlloc > 0 ? '#10b981' : '#ef4444' }}>{item.availableAfterAlloc?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#143A6B', borderLeft: '2px solid var(--border-color)' }}>{item.issuedQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: item.balanceAfterIssue > 0 ? '#06b6d4' : '#ef4444' }}>{item.balanceAfterIssue?.toLocaleString()}</td>
                        <td className="p-2 text-center" style={{ color: 'var(--text-primary)' }}>{item.poCount}</td>
                      </tr>
                    ))}
                  </tbody>
                  {!invChartFilter && (
                    <tfoot>
                      <tr style={{ borderTop: '2px solid var(--border-color)', background: 'var(--card-bg-alt)' }}>
                        <td className="p-2 font-bold" style={{ color: 'var(--text-primary)' }} colSpan={2}>TOTAL ({inventory.mCodeCount} codes)</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#F59206' }}>{inventory.totals.tagMtoQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#a855f7' }}>{inventory.totals.markBoqQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.poOrdered?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#f59e0b' }}>{inventory.totals.inTransit?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#eab308', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.stockQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#0EA5E9' }}>{(inventory.totals.warehouseStock ?? 0).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#0F766E' }}>{(inventory.totals.siteStationStock ?? 0).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#84cc16' }}>{inventory.totals.qaAccepted?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.hardAllocated?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#10b981' }}>{inventory.totals.availableAfterAlloc?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#143A6B', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.issuedQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#06b6d4' }}>{inventory.totals.balanceAfterIssue?.toLocaleString()}</td>
                        <td className="p-2 text-center font-bold" style={{ color: 'var(--text-primary)' }}>{inventory.totalPOs}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No inventory data available</p>
          )}
        </div>
      )}

      {/* RUN HISTORY */}
      {tab === 'runs' && (
        <div className="space-y-2">
          {runs.length === 0 && <p className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>No allocation runs yet</p>}
          {runs.map(run => {
            const isExpanded = expandedRun === run.runId;
            return (
              <div key={run.runId} className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <button className="w-full flex items-center justify-between px-3 py-2 text-left" onClick={() => setExpandedRun(isExpanded ? null : run.runId)} data-testid={`run-row-${run.runId}`}>
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="w-3 h-3" style={{ color: '#F59206' }} /> : <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
                    <span className="text-[10px] font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{run.runId}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: `${RUN_STATUS_COLORS[run.runStatus] || '#94a3b8'}20`, color: RUN_STATUS_COLORS[run.runStatus] || '#94a3b8' }}>{run.runStatus}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px]" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>{run.runType}</span>
                  </div>
                  <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{run.durationMs ? `${run.durationMs}ms` : ''} | {String(run.cycleDate || '').slice(0, 10)}</span>
                </button>
                {isExpanded && run.results && (
                  <div className="px-3 pb-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]" style={{ borderTop: '1px solid var(--border-color)' }}>
                    {run.results.tagsProcessed != null && <div><span style={{ color: 'var(--text-muted)' }}>Tags Processed:</span> <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{run.results.tagsProcessed}</span></div>}
                    {run.results.markNumbersProcessed != null && <div><span style={{ color: 'var(--text-muted)' }}>Marks Processed:</span> <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{run.results.markNumbersProcessed}</span></div>}
                    {run.results.allocationsCreated != null && <div><span style={{ color: 'var(--text-muted)' }}>Allocations:</span> <span className="font-bold" style={{ color: '#22c55e' }}>{run.results.allocationsCreated}</span></div>}
                    {run.results.marksFullyAllocated != null && <div><span style={{ color: 'var(--text-muted)' }}>Fully Allocated:</span> <span className="font-bold" style={{ color: '#22c55e' }}>{run.results.marksFullyAllocated}</span></div>}
                    {run.results.errors?.length > 0 && (
                      <div className="col-span-full">
                        <span className="font-semibold" style={{ color: '#ef4444' }}>Errors ({run.results.errors.length}):</span>
                        {run.results.errors.slice(0, 3).map((e, i) => <p key={i} className="text-[9px]" style={{ color: '#ef4444' }}>{e.tagId}: {e.error}</p>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* LEDGER */}
      {tab === 'ledger' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={ledgerFilter.allocationType} onChange={e => setLedgerFilter(f => ({ ...f, allocationType: e.target.value }))} className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="ledger-filter-type">
              <option value="">All Types</option>
              <option value="SOFT">Soft</option>
              <option value="HARD">Hard</option>
            </select>
            <input value={ledgerFilter.structuralTag} onChange={e => setLedgerFilter(f => ({ ...f, structuralTag: e.target.value }))} placeholder="Tag #" className="text-[10px] px-2 py-1 rounded border w-24" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="ledger-filter-tag" />
            <input value={ledgerFilter.markNumber} onChange={e => setLedgerFilter(f => ({ ...f, markNumber: e.target.value }))} placeholder="Mark #" className="text-[10px] px-2 py-1 rounded border w-24" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="ledger-filter-mark" />
            <span className="text-[10px] ml-auto" style={{ color: 'var(--text-muted)' }}>Total: {ledger.total} entries</span>
          </div>
          <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 z-10">
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    {['Run ID', 'Type', 'Action', 'Tag', 'Mark', 'mCode', 'Qty', 'Weight', 'PO#', 'Rank', 'Time'].map(h => (
                      <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(ledger.entries || []).map((e, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td className="px-2 py-1 font-mono text-[9px]" style={{ color: 'var(--text-primary)' }}>{e.runId}</td>
                      <td className="px-2 py-1"><span className="px-1 py-0.5 rounded text-[9px]" style={{ background: e.allocationType === 'SOFT' ? '#3b82f620' : '#22c55e20', color: e.allocationType === 'SOFT' ? '#3b82f6' : '#22c55e' }}>{e.allocationType}</span></td>
                      <td className="px-2 py-1 text-[9px]" style={{ color: e.action === 'DE_ALLOCATE' ? '#ef4444' : 'var(--text-primary)' }}>{e.action}</td>
                      <td className="px-2 py-1 font-mono text-[9px]" style={{ color: '#F59206' }}>{e.structuralTag}</td>
                      <td className="px-2 py-1 font-mono text-[9px]" style={{ color: 'var(--text-primary)' }}>{e.markNumber || '-'}</td>
                      <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-muted)' }}>{e.mCode}</td>
                      <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{e.quantity}</td>
                      <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{Number(e.weight || 0).toFixed(1)} kg</td>
                      <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-muted)' }}>{e.poNumber}</td>
                      <td className="px-2 py-1 text-center" style={{ color: 'var(--text-muted)' }}>{e.allocationSortRank || '-'}</td>
                      <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-muted)' }}>{String(e.timestamp || '').slice(11, 19)}</td>
                    </tr>
                  ))}
                  {(ledger.entries || []).length === 0 && <tr><td colSpan={11} className="text-center py-6" style={{ color: 'var(--text-muted)' }}>No ledger entries</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* FORECAST */}
      {tab === 'forecast' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={generateForecast} disabled={forecastLoading} className="text-[10px] h-7" style={{ background: '#F59206' }} data-testid="swm-gen-forecast">
              {forecastLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} Generate Forecast
            </Button>
          </div>
          {forecastLoading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>}
          {!forecastLoading && forecast && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <ForecastCard label="Tags Ready This Period" value={forecast.overallForecast?.tagsReadyThisPeriod || 0} color="#22c55e" />
                <ForecastCard label="Fab Progress" value={`${(forecast.overallForecast?.cumulativeFabProgressPct || 0).toFixed(1)}%`} color="#143A6B" />
                <ForecastCard label="Erection Progress" value={`${(forecast.overallForecast?.cumulativeErectionProgressPct || 0).toFixed(1)}%`} color="#f59e0b" />
                <ForecastCard label="Avg Fab Cycle" value={`${(forecast.overallForecast?.avgFabCycleTimeDays || 0).toFixed(0)} days`} color="#3b82f6" />
              </div>
              <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <div className="px-3 py-1.5 text-[11px] font-bold" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}>Tag Forecasts</div>
                <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
                  <table className="w-full text-[10px]">
                    <thead className="sticky top-0 z-10">
                      <tr style={{ background: 'var(--card-bg-alt)' }}>
                        {['Tag', 'Priority', 'Soft %', 'Hard %', 'Fab Start', 'Fab Complete', 'Erec Start', 'Erec Complete', 'Confidence'].map(h => (
                          <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(forecast.tagForecasts || []).map(f => (
                        <tr key={f.structuralTag} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td className="px-2 py-1 font-mono" style={{ color: '#F59206' }}>{f.structuralTag}</td>
                          <td className="px-2 py-1"><span className="px-1 py-0.5 rounded text-[9px] font-bold" style={{ color: ['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][f.priority] || 'var(--text-muted)' }}>P{f.priority || '?'}</span></td>
                          <td className="px-2 py-1 font-mono" style={{ color: '#3b82f6' }}>{(f.currentSoftAllocPct || 0).toFixed(0)}%</td>
                          <td className="px-2 py-1 font-mono" style={{ color: '#22c55e' }}>{(f.currentHardAllocPct || 0).toFixed(0)}%</td>
                          <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-primary)' }}>{f.forecastFabStartDate?.slice(0, 10) || '-'}</td>
                          <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-primary)' }}>{f.forecastFabCompleteDate?.slice(0, 10) || '-'}</td>
                          <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-primary)' }}>{f.forecastErectionStartDate?.slice(0, 10) || '-'}</td>
                          <td className="px-2 py-1 text-[9px]" style={{ color: 'var(--text-primary)' }}>{f.forecastErectionCompleteDate?.slice(0, 10) || '-'}</td>
                          <td className="px-2 py-1"><span className="px-1 py-0.5 rounded text-[9px] font-bold" style={{ background: f.forecastConfidence === 'HIGH' ? '#22c55e20' : f.forecastConfidence === 'MEDIUM' ? '#f59e0b20' : '#ef444420', color: f.forecastConfidence === 'HIGH' ? '#22c55e' : f.forecastConfidence === 'MEDIUM' ? '#f59e0b' : '#ef4444' }}>{f.forecastConfidence}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          {!forecastLoading && !forecast && <p className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>No forecast generated yet. Click "Generate Forecast" to create one.</p>}
        </div>
      )}
    </div>
  );
}

function ForecastCard({ label, value, color }) {
  return (
    <div className="p-2.5 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <p className="text-[9px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-lg font-bold mt-0.5" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
    </div>
  );
}
